import { GhostAPI, PostOrPage, PostsOrPages, Tag } from '@tryghost/content-api';
import React, { memo, useEffect, useMemo, useState } from 'react';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _omit from 'lodash/omit';
import _filter from 'lodash/filter';
import _shuffle from 'lodash/shuffle';
import _findIndex from 'lodash/findIndex';
import _get from 'lodash/get';
import { DiscussionEmbed } from 'disqus-react';
import Skeleton from 'react-loading-skeleton';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router';
import ReactDOMServer from 'react-dom/server';
import { getFilterProduct, } from '../api-helpers/jomashop-graphql';
import ProductSelling from '../components/product-selling';
import { getRandomItems } from '../helpers/utils';
import withContentApi from '../hoc/withContentApi';
import Breadcrumb from '../components/breadcrumb';
import RecentPublicFeeds from '../layout/article-details/recent-public-feeds';
import AuthorCard from '../components/author-card';
import { getDate } from '../helpers/date-time';
import { readingTime } from '@tryghost/helpers/lib';
import StoryCard from '../components/story-card';
import FooterIcon from '../components/footer-icon';
import IVideoYoutube, { IGetVideosResponse } from '../model/youtube-video-response';
import { getVideo } from '../api-helpers/youtube-video';
import { AxiosResponse } from 'axios';
import VideoCardV2 from '../components/video-card-v2';
import { descriptionNoLink, generateSourceSet, getArticleLink, getDisplayTagName, getTag } from '../helpers/article-data';
import { getPosts, isGuideArticle } from '../api-helpers/data-transformer';
import { IAppContextStore, useAppContext } from '../context/app-context';
import { Link } from 'react-router-dom';
import ProductRecommendation from '../components/product-recommendation';
import { EScreen } from '../helpers/screen-helper';
import useWindowDimensions from '../custom-hooks/useWindowDimensions';
import './article-details-custom-styles.css';
import { IRecommendProduct, searchForRecommend } from '../api-helpers/trending-api';


interface IProps {
  api?: GhostAPI;
}

interface IRouteParams {
  slug: string;
  tag: string;
}

interface IArticleDetailHeaderProps {
  articleData?: PostOrPage;
  isLoading: boolean;
}

enum PostAction {
  NEWER= 'newer',
  OLDER= 'older'
}

interface IDirectionPost {
  currentPostIdx: number
  newerPostIdx: number
  olderPostIdx: number
}

const isFulfilled = <T,>(
    result: PromiseSettledResult<T>
): result is PromiseFulfilledResult<T> => result.status === 'fulfilled';

const isNotNull = <T,>(value: T | null | undefined): value is T => {
  return value !== null && value !== undefined;
}

const RelatedArticlesLength: number = 3;

const ArticleDetailHeader: React.FC<IArticleDetailHeaderProps> = memo(({ articleData, isLoading }) => {
  const screenSize: EScreen = useWindowDimensions();
  const countSkeletonHeading =  screenSize === EScreen.xs ? 4 : 2;
  
  return (
    <>
      <div className='flex flex-col pt-6 pb-5 sm:pt-34px sm:pb-4 md:pb-7 lg:pb-8 text-center'>
        <Link to={`/tags/${getTag(articleData)?.slug}`}>
          <div className='text-jm-black text-sub-heading uppercase mb-7px'>{getDisplayTagName(articleData) || (isLoading === false ? '' : <Skeleton />)}</div>
        </Link>
        <div className='text-jm-black-title text-38 font-heading font-bold mb-10px'>{articleData?.title || <Skeleton count={countSkeletonHeading} />}</div>
        <div className='mx-auto'>
          <AuthorCard
            authors={articleData?.authors || []}
            date={articleData?.published_at ? getDate(new Date(articleData?.published_at)) : ''}
            speedReading={articleData ? readingTime(articleData as PostOrPage, { minute: 'A min read', minutes: '% mins read' }) : ''} />
        </div>
        <div className='mt-5 sm:mt-4 aspect-main-img overflow-hidden w-full'>
          <img
            loading='lazy'
            srcSet={generateSourceSet(articleData?.feature_image || '')}
            src={articleData?.feature_image || 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='}
            alt={articleData?.feature_image_alt || articleData?.feature_image_caption || ''}
            className={`w-full aspect-main-img bg-jm-slide-background object-cover object-center ${_isEmpty(articleData) ? 'animate-pulse' : ''}`} />
        </div>
      </div>
    </>
  )
});

const ArticleDetailsPage: React.FC<IProps> = ({ api }) => {
  const { slug } = useParams<keyof IRouteParams>();
  const [articleData, setArticleData] = useState<PostOrPage>();
  const [videoOfWeek, setVideoOfWeek] = useState<IVideoYoutube>();
  const [relatedArticles, setRelatedArticles] = useState<PostOrPage[]>();
  const [relatedArticlesForDisplay, setRelatedArticlesForDisplay] = useState<PostOrPage[]>([]);
  const { allPosts }: IAppContextStore = useAppContext();
  const [productsYouMayLike, setProductsYouMayLike] = useState<IRecommendProduct[]>([]);
  const [directionPost, setDirectionPost] = useState<IDirectionPost>({
    currentPostIdx: 0,
    newerPostIdx: 0,
    olderPostIdx: 0,
  })
  const [documentHTML, setDocumentHTML] = useState('')
  const location = useLocation();
  const navigate = useNavigate();
  const isLoading: boolean = useMemo(() => _isEmpty(articleData) === true, [articleData]);
  const screenSize: EScreen = useWindowDimensions();
  
  const handleDirectionPost = (direction: string) => {
    if (direction === PostAction.NEWER) {
      setDirectionPost((prevState) => ({
        ...prevState,
        newerPostIdx: prevState.newerPostIdx - 1,
      }))
    }
    
    if (direction === PostAction.OLDER) {
      setDirectionPost((prevState) => ({
        ...prevState,
        olderPostIdx: prevState.olderPostIdx + 1,
      }))
    }
  }
  
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [slug]);

  useEffect(() => {
    api?.posts.read({ slug: slug as string }, { include: ['authors', 'tags'] }).then((res: PostOrPage) => {
      setArticleData(res);
      setDocumentHTML(String(res.html))
    }).catch(e => {
      if (e?.errorType === 'NotFoundError' || e?.name === 'NotFoundError') {
        navigate('/not-found');
      }
    });

    return () => { };
  }, [api, slug, navigate]);

  useEffect(() => {
    if (_isEmpty(articleData) === false) {
      api?.posts.browse({ limit: RelatedArticlesLength + 1, filter: `tags:[${_map(articleData?.tags, (tag: Tag) => tag.slug).join(',')}]`, include: ['authors', 'tags'] }).then((res: PostsOrPages) => {
        setRelatedArticles(_filter(_map(_omit(res, 'meta')) as PostOrPage[], (article: PostOrPage) => article.id !== articleData?.id));
      });
    }

    return () => { };
  }, [api, articleData]);

  useEffect(() => {
    if (_isEmpty(videoOfWeek) === true) {
      getVideo(1).then((res: AxiosResponse<IGetVideosResponse | IVideoYoutube[]>) => {
        const resData: IVideoYoutube[] = res.data as IVideoYoutube[];
        setVideoOfWeek(resData[0]);
      });
    }

    return () => { };
  }, [videoOfWeek]);

  const currentUrl: string = useMemo(() => `${window.location.protocol}//${window.location.host}${location.pathname}`, [location]);
  const isGuide: boolean = useMemo(() => isGuideArticle(articleData as PostOrPage), [articleData]);
  const sectionName = useMemo(() => isGuide ? '/guides' : '/articles', [isGuide]);

  useEffect(() => {
    if (_isEmpty(articleData) === false && _isEmpty(allPosts) === false && _isEmpty(api) === false && typeof relatedArticles !== 'undefined') {
      if (relatedArticles.length >= RelatedArticlesLength) {
        setRelatedArticlesForDisplay(relatedArticles);
      } else {
        const listSlugs: string[] = _map(_shuffle(_filter(allPosts, (post: PostOrPage) => post.slug !== articleData?.slug)), 'slug').slice(0, RelatedArticlesLength - relatedArticles.length);

        api?.posts.browse({ limit: 'all', filter: `slug:[${listSlugs.join(',')}]`, include: ['tags', 'authors'] }).then((res: PostsOrPages) => {
          setRelatedArticlesForDisplay(getPosts(res));
        });
      }
    }
    return () => { };
  }, [relatedArticles, setRelatedArticlesForDisplay, articleData, allPosts, api]);
  
  const nextArticle: PostOrPage = useMemo(() => _get(allPosts, (directionPost.newerPostIdx === 0 ? allPosts.length : directionPost.newerPostIdx) - 1), [allPosts, directionPost.newerPostIdx]);
  const olderArticle: PostOrPage = useMemo(() => _get(allPosts, (directionPost.olderPostIdx === allPosts.length - 1 ? -1 : directionPost.olderPostIdx) + 1), [allPosts, directionPost.olderPostIdx]);
  const isNewestPost: boolean = useMemo(() => directionPost.newerPostIdx === 0, [directionPost.newerPostIdx]);
  const isOldestPost: boolean = useMemo(() => directionPost.olderPostIdx === allPosts.length - 1, [directionPost.olderPostIdx, allPosts]);
  const displayTitle: string = useMemo(() => articleData?.title?.split(' ').slice(0, 6).join(' ') || articleData?.title || '', [articleData]);
  const currentTag: Tag = useMemo(() => getTag(articleData), [articleData]);
  const recommendTag: Tag = useMemo(() => {
    const articleTags = articleData?.tags?.filter(tag => !tag.name?.startsWith('#'))
    const articleClone = {...articleData, tags: articleTags}

    return _get(articleClone, 'tags[1]', _get(articleClone, 'tags[0]', currentTag))
  }, [articleData, currentTag]);
  
  const canonical = useMemo(() => `https://www.jomashop.com/blog${sectionName}/${articleData?.slug}`, [sectionName, articleData]);
  
  useEffect(() => {
    const currentPostIdx = _findIndex(allPosts, ['slug', slug], 0)
    if (currentPostIdx !== -1) {
      setDirectionPost((prevState) => ({
        ...prevState,
        currentPostIdx: currentPostIdx,
        newerPostIdx: currentPostIdx,
        olderPostIdx: currentPostIdx
      }))
    }
  }, [slug, allPosts])
  
  useEffect(() => {
    if (_isEmpty(recommendTag) === false) {
      searchForRecommend(recommendTag?.name || '', 6).then((res: AxiosResponse<IRecommendProduct[]>) => {
        setProductsYouMayLike(res.data);
      });
    }
  }, [recommendTag]);

  const displayDescription = useMemo(() => {
    if (articleData?.excerpt) {
      return articleData?.excerpt?.split('\n\n').join(' ');
    }

    return (articleData?.html || '').replaceAll('<[^>]*>', '').substring(0, 255);
  }, [articleData]);
  
  useEffect(() => {
    if (!articleData?.html) return
    
    const tagNames = articleData.tags?.filter(tag => tag.name !== "#category:article")
    const htmlString = articleData?.html;
    const parser = new DOMParser();
    
    const doc = parser.parseFromString(htmlString, 'text/html');
    const idEls: NodeListOf<HTMLAnchorElement> = doc.querySelectorAll('[id]');
    const figureTags: NodeListOf<HTMLElement> = doc.querySelectorAll('figure');
    
    const productsByTagNameQuery: Record<string, any[]> = {}
    
    if (tagNames && tagNames.length) {
      Promise.allSettled(tagNames?.map(tagName => tagName && getFilterProduct(tagName.name))).then((results) => {
        const fulfilledData = results.filter(isFulfilled).map(data =>
           data?.value?.data?.data?.suggestions?.data
        ).filter(isNotNull)

        if (!fulfilledData.length) return

        fulfilledData.forEach((data) => {
          const queryData = data[0]
          productsByTagNameQuery[queryData.id] = data
        })


        idEls.forEach((els) => {
          const randomTagNameIdx = Math.floor(Math.random() * Object.keys(productsByTagNameQuery).length);
          const randomTagName = Object.keys(productsByTagNameQuery)
          const randomProducts = productsByTagNameQuery[randomTagName[randomTagNameIdx]]

          if (randomProducts.length) {
            const products = randomProducts.length > 3 ? getRandomItems(randomProducts, 3) : randomProducts

            const jsxString = ReactDOMServer.renderToString(
              <>
                {products.map(product => <ProductSelling key={product.model_id} productDetail={product}/>)}
              </>
            )
            
            let nextEl = els.nextElementSibling;
            while (nextEl) {
              if (nextEl.id) {
                break;
              }
              nextEl = nextEl.nextElementSibling;
            }
            
            if (nextEl) nextEl.insertAdjacentHTML('beforebegin', jsxString)
          }
        })
        
        if (!idEls.length && figureTags.length) {
          figureTags.forEach((els, idx) => {
            const randomTagNameIdx = Math.floor(Math.random() * Object.keys(productsByTagNameQuery).length);
            const randomTagName = Object.keys(productsByTagNameQuery)
            const randomProducts = productsByTagNameQuery[randomTagName[randomTagNameIdx]]
            
            if (randomProducts.length) {
              const products = randomProducts.length > 3 ? getRandomItems(randomProducts, 3) : randomProducts
              const jsxString = ReactDOMServer.renderToString(
                <div>
                  {products.map(product => <ProductSelling key={product.model_id} productDetail={product} />)}
                </div>
              )
              
              let nextEl = els.nextElementSibling;
              while (nextEl) {
                if (nextEl.tagName.toLowerCase() === 'figure') {
                  break;
                }
                nextEl = nextEl.nextElementSibling;
              }
              
              if (nextEl && nextEl.tagName.toLowerCase() === 'figure') {
                nextEl.insertAdjacentHTML('beforebegin', jsxString)
              }
            }
          })
        }
        
        setDocumentHTML(doc.body.innerHTML)
      })
    }
  }, [articleData?.html, articleData?.tags])

  const countSekletonBreadcrumb: number = useMemo(() => {
    switch (screenSize) {
      case EScreen.xs:
        return 2;
      default:
        return 1;
    }
  }, [screenSize]);

  return (
    <div className='flex flex-col'>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{articleData?.title}</title>
        <meta name='description' content={descriptionNoLink(displayDescription).trim()} />
        <link rel='amphtml' href={`${canonical}/amp/`} />
        <link rel='canonical' href={canonical} />
      </Helmet>
      {
        isLoading ? <Skeleton width={350} count={countSekletonBreadcrumb} /> : <Breadcrumb data={[
          {
            text: isGuide ? 'Guides' : 'Articles',
            link: sectionName
          },
          {
            text: `${displayTitle}${displayTitle.length < (articleData?.title?.length || 0) ? '...' : ''}`,
            link: ''
          }
        ]} />
      }
      <div className='flex flex-col sm:mx-auto w-full'>
        <div className='hidden lg:block'>
          <ArticleDetailHeader articleData={articleData} isLoading={isLoading} />
        </div>
        <div className='flex flex-col sm:flex-row sm:space-x-4 md:space-x-7 lg:space-x-8'>
          <div className='sm:w-2/3'>
            <div className='lg:hidden'>
              <ArticleDetailHeader articleData={articleData} isLoading={isLoading} />
            </div>
            <div className='content-wrapper mb-6'>
              {isLoading ? <Skeleton count={5} /> : <div className='content-inner' dangerouslySetInnerHTML={{ __html: documentHTML || '' }} />}
            </div>
            <div className='flex space-x-3 mb-3'>
              <FooterIcon links={[
                `https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`,
                `https://twitter.com/intent/tweet?via=Jomashop&text=${articleData?.title}%20${window.location.href}`,
                `https://www.instagram.com/?url=${window.location.href}`,
                `http://pinterest.com/pin/create/button/?url=${encodeURI(window.location.href)}&media=${encodeURI(articleData?.feature_image || '')}&description=${encodeURI(articleData?.excerpt || '')}`]} inverted />
            </div>
            <div className={`flex bg-jm-light p-5 -mx-16px sm:mx-0 mt-29px mb-27px lg:space-x-3 ${isOldestPost || isNewestPost ? 'justify-center' : 'justify-between'}`}>
              <div className={`flex-col ${isNewestPost ? 'hidden' : 'flex'} ${isOldestPost ? 'mx-auto' : ''}`}>
                <div
                  onClick={() => handleDirectionPost(PostAction.NEWER)}
                  className={`text-jm-gray-sub-content flex items-center cursor-pointer ${isOldestPost ? 'justify-center' : ''} ${directionPost.newerPostIdx === 1 && 'pointer-events-none'}`}
                >
                  <span className='text-21'>&larr;</span>
                  <span className='uppercase ml-3'>Newer Post</span>
                </div>
                <Link className='text-jm-black-title font-bold mt-1' to={getArticleLink(nextArticle)}>
                  {nextArticle?.title}
                </Link>
              </div>
              <div className={`flex-col ${isOldestPost ? 'hidden' : 'flex'} ${isNewestPost ? 'mx-auto' : ''}`}>
                <div
                  onClick={() => handleDirectionPost(PostAction.OLDER)}
                  className={`text-jm-gray-sub-content flex items-center cursor-pointer ${isNewestPost ? 'justify-center' : 'justify-end'} ${directionPost.olderPostIdx === allPosts.length - 2 && 'pointer-events-none'}`}
                >
                  <span className='uppercase mr-3'>Older Post</span>
                  <span className='text-21'>&rarr;</span>
                </div>
                <Link className='text-jm-black-title font-bold mt-1 text-right' to={getArticleLink(olderArticle)}>
                  {olderArticle?.title}
                </Link>
              </div>
            </div>
            {
              !isLoading && (
                <div>
                  <DiscussionEmbed
                    shortname='jomashop'
                    config={
                      {
                        url: currentUrl,
                        identifier: articleData?.id,
                        title: articleData?.title,
                      }
                    }
                  />
                </div>
              )
            }
          </div>
          <div className='sm:w-1/3 sm:py-34px flex flex-col lg:py-0'>
            {
              typeof currentTag !== 'undefined' && (
                <div className='block sm:hidden my-3'>
                  <ProductRecommendation
                    products={productsYouMayLike.slice(-4)}
                    title='Items you may like'
                    isRecommendProducts
                  />
                </div>
              )
            }
            {articleData && <RecentPublicFeeds title='Recent Publications' excludedArticles={[articleData]} customTitleClasses='mb-3px sm:mb-5' />}
            {
              !isGuide && typeof currentTag !== 'undefined' && (
                <div className='sm:mb-3 sm:mt-6'>
                  <ProductRecommendation
                    products={productsYouMayLike.slice(0, 2)}
                  />
                </div>
              )
            }
            <div className='flex flex-col mt-42px sm:mt-3'>
              <div className='text-trending-title font-bold mb-7 sm:mb-5'>You may also like</div>
              <div className='space-y-3 flex flex-col'>
                {
                  _map(_isEmpty(relatedArticles) ? new Array(3) : relatedArticlesForDisplay.slice(0, 3), (article: PostOrPage) => (
                    <StoryCard key={article?.id || `articles-details-${Math.random()}`} article={article} />
                  ))
                }
              </div>
            </div>
            <div className='flex flex-col mt-3 sm:mt-30px mb-16 sm:mb-0'>
              <div className='text-trending-title font-bold mb-23px sm:mb-29px'>Video of the week</div>
              <VideoCardV2
                playOnNewWindow
                article={videoOfWeek as IVideoYoutube}
              />
            </div>
          </div>
        </div>
        {
          typeof currentTag !== 'undefined' && (
            <>
              <div className='hidden sm:block lg:hidden my-3'>
                <ProductRecommendation
                  products={productsYouMayLike.slice(-3)}
                  title='Items you may like'
                  isRecommendProducts
                />
              </div>
              <div className='hidden lg:block my-3'>
                <ProductRecommendation
                  products={productsYouMayLike.slice(-4)}
                  title='Items you may like'
                  isRecommendProducts
                />
              </div>
            </>
          )
        }
      </div>
    </div>
  )
};

export default memo(withContentApi<IProps>(ArticleDetailsPage));
